import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { DataTag } from '@/components/DataTag/DataTag'
import SkeletonTable from '@/components/SkeletonTable'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { routeToAccountManagementWithPrefilledEmail } from '@/features/AccountManagement/pages/AccountManagement'
import styles from '@/features/ClusterConfigs/pages/ClusterConfigRevisions/clusterConfigsRevisions.module.css'
import { hivesOverviewClusterDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewClusterDetails'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import {
  flattenHivesResponse,
  getBgColorForStatus,
} from '@/features/HivesOverview/util'
import { useFindHives } from '@/generated/http-clients/v2ApiComponents'
import { FIRST_PAGE } from '@/helpers/constants'
import { toaster } from '@/hooks/useToast/toastUtils'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import {
  Card,
  HStack,
  Icon,
  Input,
  InputGroup,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import debounce from 'lodash/debounce'
import { Search } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const hivesOverviewRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/',
  component: HivesOverviewsTable,
})

// NOTE: Exported only for testing purposes, DO NOT import this component directly.
export function HivesOverviewsTable() {
  const [page, setPage] = useState(1) // Current page
  const [filter, setFilter] = useState('') // Filter text
  const [searchQuery, setSearchQuery] = useState('') // search query based on the filter text
  const hivesRequest = useFindHives({
    queryParams: { ...(searchQuery ? { searchQuery } : { page }) },
  })
  const navigate = useNavigate()

  const { t } = useTranslation(undefined, { useSuspense: false })

  const debouncedSetSearchQuery = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 300),
    [],
  )

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setFilter(value) // Update input value immediately
    debouncedSetSearchQuery(value) // Debounce the search query update
  }

  // clean up the debounced function when the component unmounts
  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel()
    }
  }, [debouncedSetSearchQuery])

  function handleNextPage() {
    if (hivesRequest.data?.has_next_page) {
      setPage((prev) => prev + 1)
    }
  }

  function handlePreviousPage() {
    if (page > FIRST_PAGE) {
      setPage((prev) => prev - 1)
    }
  }

  function goToFirstPage() {
    setPage(FIRST_PAGE)
  }

  function goToLastPage() {
    setPage(hivesRequest.data?.total_pages ?? page)
  }

  function routeToClusterDetails(orgId: string, clusterId: string | undefined) {
    if (clusterId) {
      navigate({
        to: hivesOverviewClusterDetailsRoute.to,
        params: {
          orgId,
          clusterId,
        },
      })
    } else {
      toaster.create({
        title: 'Cluster currently not accessible.',
        type: 'warning',
      })
    }
  }

  function routeToOrganizationDetails(orgId: string) {
    navigate({
      to: hivesOverviewOrganizationDetailsRoute.to,
      params: {
        orgId,
      },
    })
  }

  /**
   * Memo to hold the flattened data to make it easier to iterate over in the table.
   */
  const hivesOverviewTableData = useMemo(() => {
    return flattenHivesResponse(hivesRequest.data?.hives)
  }, [hivesRequest])

  const filteredHives = hivesOverviewTableData

  const skeletonWidths = [120, 250, 150, 100, 100, 80, 200, 100]

  return (
    <PageLayout title={t('sidebar.runningHives')}>
      <VStack mb={4} width="50%" gap={2} alignItems="start">
        <InputGroup
          startElement={<Icon as={Search} />}
          startElementProps={{ pointerEvents: 'none' }}
        >
          <Input
            placeholder="Search for organization ID or name, cluster ID, or email..."
            value={filter}
            onChange={handleFilterChange}
            backgroundColor="background.bg-base"
          />
        </InputGroup>
        <Text mt={0} color="text.text-subtle" fontSize="xs" pl={4}>
          Organization and cluster info can be partial, but email must be a full
          address (e.g., user@example.com)
        </Text>
      </VStack>
      <Card.Root w="100%">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Cluster ID</Table.ColumnHeader>
              <Table.ColumnHeader>Cluster Name</Table.ColumnHeader>
              <Table.ColumnHeader>Org ID</Table.ColumnHeader>
              <Table.ColumnHeader>Company Name </Table.ColumnHeader>
              <Table.ColumnHeader>Plan</Table.ColumnHeader>
              <Table.ColumnHeader>Apiary</Table.ColumnHeader>
              <Table.ColumnHeader>Email</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {hivesRequest.isLoading && (
              <SkeletonTable skeletonWidths={skeletonWidths} />
            )}

            {hivesRequest.isError && (
              <Table.Row>
                <Table.Cell colSpan={8}>
                  <ApiErrorState
                    apiResource={hivesRequest as unknown as UseQueryResult}
                    errorTitle="An error happened"
                    errorDescription="We failed to load the data from the server for the templates."
                  />
                </Table.Cell>
              </Table.Row>
            )}

            {hivesRequest.isSuccess &&
              (filteredHives?.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <Text>No hives found for the organization.</Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                !!hivesOverviewTableData &&
                !!filteredHives &&
                filteredHives.map((cluster, index) => (
                  <Table.Row
                    key={`${cluster.org_id}-${index}`}
                    className={styles.trAlignContentTop}
                    _hover={{
                      backgroundColor: 'var(--chakra-colors-shell-muted)',
                    }}
                  >
                    <Table.Cell data-testid="cluster-status">
                      <HStack>
                        <DataTag
                          backgroundColor={getBgColorForStatus(
                            cluster.hive_status,
                          )}
                          value={cluster.hive_status}
                          hasCopy={false}
                        >
                          {cluster.hive_status}
                        </DataTag>
                      </HStack>
                    </Table.Cell>

                    <Table.Cell data-testid="cluster-id">
                      {cluster?.cluster_id ? (
                        <DataTag
                          onClickBody={() =>
                            routeToClusterDetails(
                              cluster.org_id,
                              cluster.cluster_id,
                            )
                          }
                          hasCopy
                          value={cluster.cluster_id}
                          maxWidthPx={200}
                        >
                          {cluster.cluster_id}
                        </DataTag>
                      ) : (
                        <DataTag>N / A</DataTag>
                      )}
                    </Table.Cell>

                    <Table.Cell data-testid="cluster-name">
                      {cluster?.cluster_name ? (
                        <DataTag hasCopy value={cluster.cluster_name}>
                          {cluster.cluster_name}
                        </DataTag>
                      ) : (
                        <Text fontSize="md" color="gray.500">
                          —
                        </Text>
                      )}
                    </Table.Cell>

                    <Table.Cell data-testid="org-id">
                      <DataTag
                        hasCopy
                        onClickBody={() =>
                          routeToOrganizationDetails(cluster.org_id)
                        }
                        value={cluster.org_id}
                      >
                        {cluster.org_id}
                      </DataTag>
                    </Table.Cell>

                    <Table.Cell data-testid="company-name">
                      <DataTag hasCopy value={cluster.company_name}>
                        {cluster.company_name}
                      </DataTag>
                    </Table.Cell>

                    <Table.Cell data-testid="cluster-plan">
                      <Text fontSize="md"> {cluster.plan} </Text>
                    </Table.Cell>

                    <Table.Cell data-testid="apiary">
                      {cluster.apiary}
                    </Table.Cell>

                    <Table.Cell data-testid="email">
                      <OrganizationMembersEmails
                        emails={cluster.emails.map(
                          ({ email_address }) => email_address,
                        )}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              ))}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {hivesRequest.isSuccess && (
        <TableFooter
          page={page}
          totalPages={hivesRequest.data?.total_pages ?? page}
          hasNextPage={!!hivesRequest.data?.has_next_page}
          onFirstPage={goToFirstPage}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onLastPage={goToLastPage}
        />
      )}
    </PageLayout>
  )
}

const MAX_EMAILS_DISPLAYED = 2

function OrganizationMembersEmails({
  emails,
}: Readonly<{ emails: (string | undefined)[] }>) {
  const navigate = useNavigate()

  const cappedEmails = emails.slice(0, MAX_EMAILS_DISPLAYED)

  const { t } = useTranslation('features')

  return (
    <HStack>
      {cappedEmails.map((email) => {
        if (!email) {
          return null
        }

        return (
          <DataTag
            key={email}
            hasCopy
            onClickBody={() =>
              routeToAccountManagementWithPrefilledEmail(navigate, email)
            }
            value={email}
          >
            {email}
          </DataTag>
        )
      })}
      {emails.length > MAX_EMAILS_DISPLAYED && (
        <Text fontSize="xs" color="gray.500">
          {t('hivesOverview.table.moreEmails', {
            amount: emails.length - MAX_EMAILS_DISPLAYED,
          })}
        </Text>
      )}
    </HStack>
  )
}
